// Phase SE-7 — Filter executor (pure TypeScript).
//
// Per Every Document/Gaps/SlipExtractor_Build_Plan_v1.0.md §7 SE-7
// + §3 invariant #5 (pure-function executor): zero side effects,
// no I/O, fully unit-testable.
//
// Takes a (legs, plan) → returns a FilterResult: which legs survived,
// which were dropped + why, and aggregate metrics. Each tool from
// §9 is implemented as a single step that filters the current
// `kept` set in place; tools execute in the order Claude emitted
// them so the plan's intent is preserved.
//
// N11 (no-trauma): no special handling here — the executor only
// applies the plan it's given. The scorer (SE-5) and intent parser
// (SE-6) are the gates that enforce N11 upstream. By the time a leg
// reaches this module, any user-history negative is already a
// weight=0 driver and the scorer has done its math.

import type { FilterPlan, FilterTool } from "./intentParser.ts";
import type { ScoredLeg, SignalSource } from "./legScorer.ts";
// T1.3 — post-filter correlation advisory pass (N12: advisory only).
import {
  annotateCorrelations,
  CORRELATION_THRESHOLD,
  pairCorrelation,
} from "./correlationDoctrine.ts";
// TPI-C4 — N-TPI-04: the system never removes a Dominant-convergence
// leg. Enforced at the executor boundary.
import { enforceDominantConvergenceProtection } from "./convergenceSignal.ts";

export interface DroppedLeg {
  leg: ScoredLeg;
  /** Which tool caused the drop. */
  source_tool: string;
  /** SE-LADDER — which elimination-ladder tier removed the leg, when the
   *  drop came from the quality gate (source_tool === "weak_link"). Lets the
   *  UI/analytics group removals by the named reason without re-parsing the
   *  prose. Absent for non-ladder drops (filters, caps, vetoes). */
  tier?: EliminationTier;
  /** User-readable reason. */
  reason: string;
  /** T1.4 — optional read-only sharper-alternative reference at a
   *  comparable odds bracket. Metadata about the market, NOT a new
   *  bet — the kept subset is never affected (anti-scope). */
  suggested_alternative?: {
    label: string;
    odds: number;
    source: "daily_acca";
  };
}

/** A kept leg that weak-link analysis flagged as risky but couldn't
 *  remove because removing it would make the user's target unreachable. */
export interface WeakLinkWarning {
  index: number;
  fixture: string;
  market: string;
  selection: string;
  odds: number;
  pWin: number;
  damage_ratio: number;
  improvement_factor: string;
  /** SE-LADDER — the tier that flagged this leg (it was kept anyway because
   *  removing it would breach the user's odds target / min-leg floor). */
  tier?: EliminationTier;
  reason: string;
}

export interface FilterResult {
  kept: ScoredLeg[];
  dropped: DroppedLeg[];
  /** Multiplied odds of kept legs. Null when kept is empty. */
  combined_odds_kept: number | null;
  /** Independent-product hit probability of the kept slip (assumes
   *  no correlation between legs). Null when kept is empty. */
  hit_probability_kept: number | null;
  /** EV of the kept slip = combined_odds × hit_probability − 1.
   *  Null when kept is empty. */
  expected_value_kept: number | null;
  /** A flag the UI can use to render the "no legs survived" state. */
  empty: boolean;
  /** True when the plan called decline() — kept is empty + reason
   *  surfaces in `declined_reason`. */
  declined: boolean;
  declined_reason: string | null;
  /** Tool calls actually applied (after the executor pruned invalid
   *  ones). Echoed for the UI + history view. */
  applied_tools: FilterTool[];
  /** UA-2: Weak Link Analysis — legs REMOVED by the quality gate
   *  (pass 1). These are genuine liabilities the system cut. */
  weak_links?: {
    removed: DroppedLeg[];
    combined_improvement: string;
    slip_hit_prob_before: number;
    slip_hit_prob_after: number;
  } | null;
  /** Kept legs that are weak but needed to reach the user's target.
   *  Shown as warnings in the KEPT section. */
  weak_link_warnings?: WeakLinkWarning[] | null;
  /** UA-1: Swap suggestions for weak kept legs — stronger alternatives from substrate. */
  swap_suggestions?: Array<{
    leg_index: number;
    current: { fixture: string; market: string; selection: string; odds: number; pWin: number };
    swap: {
      fixture: string;
      market: string;
      selection: string;
      odds: number;
      pWin: number;
      source: string;
    };
    delta_pWin_pp: number;
  }> | null;
  /** UA-3: Correlation Shield — correlated leg pairs that reduce real probability. */
  correlation_shield?: {
    pairs: Array<{
      leg_a: { index: number; fixture: string; market: string; selection: string };
      leg_b: { index: number; fixture: string; market: string; selection: string };
      reason: string;
      naive_combined_pct: number;
      actual_combined_pct: number;
      tax_pp: number;
      correlation_factor: number;
    }>;
    total_pairs: number;
    total_tax_pp: number;
    slip_overstatement_pct: number;
  } | null;
  /** SE-LADDER — honest coverage advisory. Set when NONE of the kept legs are
   *  model-backed (no Fair-Price/model edge anywhere) — the slip is entirely
   *  the bookmaker's prices. Pruning can only reduce the damage, not create
   *  value that isn't there. Null when at least one kept leg has a real edge. */
  coverage_advisory?: {
    level: "none";
    kept_with_edge: number;
    kept_total: number;
    message: string;
  } | null;
  /** Present when the user requested a numeric combined-odds target. */
  target_advisory?: {
    requested: number;
    achieved: number | null;
    within_tolerance: boolean;
    reason: string;
  } | null;
}

const EMPTY_RESULT_BASE = {
  combined_odds_kept: null,
  hit_probability_kept: null,
  expected_value_kept: null,
  empty: true,
  declined: false,
  declined_reason: null,
};

/** Apply a FilterPlan to a scored-leg list. Pure, deterministic. */
export function applyFilterPlan(legs: readonly ScoredLeg[], plan: FilterPlan): FilterResult {
  // Decline branch (N09)
  if (plan.declined) {
    return {
      ...EMPTY_RESULT_BASE,
      declined: true,
      declined_reason: plan.decliningReason ?? "Plan declined.",
      kept: [],
      dropped: legs.map((leg) => ({
        leg,
        source_tool: "decline",
        reason: plan.decliningReason ?? "Plan declined.",
      })),
      applied_tools: plan.tools,
    };
  }

  const targetTool = plan.tools.find(
    (t): t is Extract<FilterTool, { name: "target_combined_odds" }> =>
      t.name === "target_combined_odds",
  );

  // Quality-first pre-pass (N-Q1): when the user is BUILDING combined odds
  // (a target / odds-cap), strip genuine value-traps BEFORE assembly — a
  // clearly −EV leg that is NOT a near-lock. Strong favourites are never
  // vetoed (keeping them is the point). Limited to odds-assembly intents so
  // pure filters / "top N by odds" keep their exact semantics. Skip the veto
  // if it would empty the slip — honest-empty stays the tools' job.
  const ASSEMBLY_INTENT = new Set<FilterTool["name"]>([
    "target_combined_odds",
    "cap_combined_odds",
  ]);
  const hasAssembly = plan.tools.some((t) => ASSEMBLY_INTENT.has(t.name));
  // SE-LADDER — "get rid of the teams likely to kill this slip": run the
  // elimination ladder as an explicit, BOUNDED user intent (no odds target).
  const pruneTool = plan.tools.find(
    (t): t is Extract<FilterTool, { name: "prune_risky_legs" }> => t.name === "prune_risky_legs",
  );
  let baseLegs: ScoredLeg[] = [...legs];
  const preDropped: DroppedLeg[] = [];
  if (hasAssembly && !targetTool) {
    const survivors = legs.filter((l) => !isHardVeto(l));
    if (survivors.length < legs.length) {
      for (const leg of legs) {
        if (isHardVeto(leg)) {
          preDropped.push({
            leg,
            source_tool: "hard_veto",
            reason: `Vetoed before assembly — ${(leg.expectedValue * 100).toFixed(1)}% expected loss on only a ${(leg.pWin * 100).toFixed(0)}% chance (not a near-lock).`,
          });
        }
      }
      baseLegs = survivors;
    }
  }

  // ── PASS 1: Elimination ladder (quality gate) ─────────────────
  // Fires for ASSEMBLY intents (target/cap combined odds) — "build me ~X
  // odds", so the gate cleans out liabilities before assembling, stopping
  // at the odds-target floor — AND for an explicit prune_risky_legs intent —
  // "get rid of the teams likely to kill this slip", stopping at a retention
  // cap so it never collapses the slip. For pure filters (select_top_n,
  // filter_by_market, etc.) the user's tool IS the filter — the gate would
  // fight with explicit intent, so it stays off.
  const weakLinkDropped: DroppedLeg[] = [];
  const weakLinkWarnings: WeakLinkWarning[] = [];
  let weakLinkPHitBefore = 0;
  let weakLinkPHitAfter = 0;
  if (hasAssembly || pruneTool) {
    const oddsTarget = extractOddsTarget(plan);
    // Prune-only (no odds target): bound removals so the slip is never gutted.
    const retentionCap =
      oddsTarget === null && pruneTool ? computePruneBudget(pruneTool, baseLegs.length) : null;
    weakLinkPHitBefore = computeHitProbability(baseLegs) ?? 0;
    const wlResult = applyWeakLinkPass(baseLegs, oddsTarget, retentionCap);
    for (const d of wlResult.removed) weakLinkDropped.push(d);
    for (const w of wlResult.warnings) weakLinkWarnings.push(w);
    baseLegs = wlResult.kept;
    weakLinkPHitAfter = computeHitProbability(baseLegs) ?? 0;
    if (weakLinkDropped.length > 0) {
      console.log(
        `[filterExecutor] weak-link pass: removed ${weakLinkDropped.length} legs, ` +
          `pHit ${(weakLinkPHitBefore * 100).toFixed(2)}%→${(weakLinkPHitAfter * 100).toFixed(2)}%`,
      );
    }
  }

  // ── PASS 2: Apply user's tools in order (target-fitting) ──────
  const runTools = (
    tools: readonly FilterTool[],
  ): { kept: ScoredLeg[]; dropped: DroppedLeg[]; applied: FilterTool[] } => {
    let k: ScoredLeg[] = [...baseLegs];
    const d: DroppedLeg[] = [];
    const a: FilterTool[] = [];
    for (const tool of tools) {
      const result = applyOneTool(k, tool);
      k = result.kept;
      for (const x of result.dropped) d.push(x);
      a.push(tool);
      if (k.length === 0) break; // dropped everything, no point continuing
    }
    return { kept: k, dropped: d, applied: a };
  };

  // An explicit odds target is a delivery requirement. Preserve hard user
  // scope constraints, then let the target optimizer rank the strongest
  // available subset. Quality filters remain preferences in this mode; they
  // must not make an otherwise reachable target fail.
  const targetScopeTools = new Set<FilterTool["name"]>([
    "filter_by_market",
    "filter_by_odds_range",
    "filter_by_league",
  ]);
  const effectiveTools = targetTool
    ? [...plan.tools.filter((tool) => targetScopeTools.has(tool.name)), targetTool]
    : plan.tools;
  let { kept, dropped, applied } = runTools(effectiveTools);

  // Fold pre-veto AND weak-link drops into the dropped set so the
  // accounting invariant (kept + dropped = all input legs) holds.
  // The UI uses source_tool to distinguish: "weak_link" = quality
  // gate (Pass 1), everything else = target-fitting (Pass 2).
  dropped = [...weakLinkDropped, ...preDropped, ...dropped];

  // T1.3 — post-filter advisory: annotate correlated same-fixture
  // pairs. Adds 0pp `correlation_warning` drivers only; never drops
  // a leg (N12). Metrics below stay on the independence assumption —
  // the warning explicitly shows the user the adjusted figure.
  kept = annotateCorrelations(kept);

  // TPI-C4 / N-TPI-04 — a Dominant convergence leg is NEVER removed
  // by the system. Pull any such leg back into kept with a conflict
  // driver, regardless of what the plan's tools did.
  {
    const prot = enforceDominantConvergenceProtection(kept, dropped);
    kept = prot.kept;
    dropped = prot.dropped;
  }

  // SE-HITFLOOR (2026-05-29 bust-streak fix) — final safety net.
  // Even after tools + vetoes + convergence protection, the kept set
  // may still have a hit probability so low it's mathematically a
  // guaranteed bust (e.g. 32 legs × 0.000 pHit). Trim the weakest
  // links until we clear a 1% floor, or fall to MIN_KEPT_AFTER_TRIM.
  // Only fires for assembly intents — pure filters / "top N by odds"
  // keep their exact semantics. Skipped when convergence-protected
  // legs would be the ones trimmed (those are immune by doctrine).
  //
  if (hasAssembly && !targetTool && kept.length > 0) {
    const before = kept.length;
    const beforePHit = computeHitProbability(kept) ?? 0;
    if (beforePHit < MIN_HIT_PROBABILITY) {
      const r = enforceHitProbabilityFloor(kept);
      // Don't trim a leg flagged as dominant-convergence-protected.
      const protectedKeys = new Set(
        kept
          .filter((l) =>
            l.drivers?.some((d) =>
              String((d as { signal?: string }).signal ?? "").includes("convergence"),
            ),
          )
          .map(
            (l) =>
              `${l.home_team ?? ""}|${l.away_team ?? ""}|${l.market ?? ""}|${l.selection ?? ""}|${l.odds ?? ""}`,
          ),
      );
      const safeTrim = r.dropped.filter((d) => {
        const k = `${d.leg.home_team ?? ""}|${d.leg.away_team ?? ""}|${d.leg.market ?? ""}|${d.leg.selection ?? ""}|${d.leg.odds ?? ""}`;
        return !protectedKeys.has(k);
      });
      const trimmedKeys = new Set(
        safeTrim.map(
          (d) =>
            `${d.leg.home_team ?? ""}|${d.leg.away_team ?? ""}|${d.leg.market ?? ""}|${d.leg.selection ?? ""}|${d.leg.odds ?? ""}`,
        ),
      );
      kept = kept.filter((l) => {
        const k = `${l.home_team ?? ""}|${l.away_team ?? ""}|${l.market ?? ""}|${l.selection ?? ""}|${l.odds ?? ""}`;
        return !trimmedKeys.has(k);
      });
      dropped = [...dropped, ...safeTrim];
      const afterPHit = computeHitProbability(kept) ?? 0;
      console.log(
        `[filterExecutor] hit-floor trim: ${before}→${kept.length} legs, ` +
          `pHit ${(beforePHit * 100).toFixed(2)}%→${(afterPHit * 100).toFixed(2)}%`,
      );
    }
  }

  // Build weak_links summary using Pass 1 metrics (captured before
  // Pass 2 tools run — the improvement is purely from quality-gate
  // removals, not conflated with target-fitting drops).
  const weakLinksSummary =
    weakLinkDropped.length > 0
      ? {
          removed: weakLinkDropped,
          combined_improvement:
            weakLinkPHitBefore > 0
              ? `${(weakLinkPHitAfter / weakLinkPHitBefore).toFixed(1)}×`
              : "—",
          slip_hit_prob_before: weakLinkPHitBefore,
          slip_hit_prob_after: weakLinkPHitAfter,
        }
      : null;

  const achieved = computeCombinedOdds(kept);
  const targetAdvisory = targetTool
    ? {
        requested: targetTool.value,
        achieved,
        within_tolerance:
          achieved !== null &&
          achieved >= targetTool.value &&
          achieved <= targetTool.value * (1 + targetTool.tolerance),
        reason:
          achieved !== null &&
          achieved >= targetTool.value &&
          achieved <= targetTool.value * (1 + targetTool.tolerance)
            ? "Target fulfilled with the strongest available subset in the requested range."
            : (computeCombinedOdds(baseLegs) ?? 0) < targetTool.value
              ? "The eligible legs in the uploaded slip do not contain enough combined odds to reach the requested target."
              : "Returned the closest available combination; the slip's discrete bookmaker prices do not form a subset inside the target range.",
      }
    : null;

  return {
    kept,
    dropped,
    combined_odds_kept: computeCombinedOdds(kept),
    hit_probability_kept: computeHitProbability(kept),
    expected_value_kept: computeExpectedValue(kept),
    empty: kept.length === 0,
    declined: false,
    declined_reason: null,
    applied_tools: applied,
    weak_links: weakLinksSummary,
    weak_link_warnings: weakLinkWarnings.length > 0 ? weakLinkWarnings : null,
    coverage_advisory: computeCoverageAdvisory(kept),
    target_advisory: targetAdvisory,
  };
}

// ─── Pass 1 — Elimination Ladder (quality gate) ──────────────────
//
// SE-LADDER (founder, 2026-06-04). Severe extractions were busting
// because edgeless / weak / shaky / correlated legs rode along to the
// target number. Instead of a single statistical quality cut, the gate
// now sheds ticket-poison legs in a FIXED PRIORITY ORDER:
//
//   1. home_or_away_trap — Double Chance 12 / Home-or-Away with no model edge
//   2. no_edge        — market-implied only, no model edge, not +EV
//   3. weak_selection — low win probability
//   4. shaky_team     — high model variance / low-confidence read
//   5. correlated     — duplicate-risk (same fixture, or same
//                       league + day + side as another kept leg)
//
// Demand-driven (founder): it eliminates worst-tier-first ONLY as far
// as the user's ask permits — the instant removing the next leg would
// make an explicit odds target unreachable (or breach the min-leg floor) it stops,
// and any remaining flagged legs become kept-but-warned. A near-lock
// favourite is never a ladder candidate (quality-first doctrine, same
// STRONG_FAVOURITE guard as the hard veto). Pure.
const WEAK_LINK_MIN_LEGS = 3;

/** The elimination-ladder tiers, highest removal priority first. */
export type EliminationTier =
  | "home_or_away_trap"
  | "no_edge"
  | "weak_selection"
  | "shaky_team"
  | "correlated";

const LADDER_ORDER: readonly EliminationTier[] = [
  "home_or_away_trap",
  "weak_selection",
  "no_edge",
  "shaky_team",
  "correlated",
];

// Tier 2 — a selection below this win probability is "weak". Kept low so
// genuine high-odds value legs (which tier 1 already exempts when they carry
// a model edge) aren't swept by a balanced slip; the demand-driven stop
// bounds how many actually get cut.
const WEAK_PWIN_FLOOR = 0.4;
// Tier 3 — a 95% CI wider than this means the model is genuinely unsure
// about the leg (a "shaky" read / high-variance team).
const SHAKY_CI_WIDTH = 0.38;

// A kept-for-target leg is only surfaced as "carried risk" when it is more
// likely to LOSE than win on its own (pWin below this). Favourites kept to
// reach the target are not flagged — they're likely to play through.
const CARRIED_RISK_PWIN = 0.5;

// Signal sources that constitute a genuine STRIKZA edge (vs a bare
// market-implied prior). A positive signed contribution from any of these
// means the engine actively backs the selection — it is NOT edgeless.
const EDGE_SOURCES: ReadonlySet<SignalSource> = new Set<SignalSource>([
  "fair_price",
  "dna",
  "daily_acca",
  "owl",
  "powerball",
  "rollover",
  "convergence_signal",
  "line_movement",
  "best_price_available",
  "strikza_product",
]);

function ciWidth(leg: ScoredLeg): number {
  const ci = leg.pWin95CI;
  if (!Array.isArray(ci) || ci.length !== 2) return 0;
  return Math.max(0, ci[1] - ci[0]);
}

function hasModelEdge(leg: ScoredLeg): boolean {
  return (leg.drivers ?? []).some(
    (d) => EDGE_SOURCES.has(d.source) && (d.contribution_pp ?? 0) > 0,
  );
}

export function isHomeOrAwayDoubleChance(leg: Pick<ScoredLeg, "market" | "selection">): boolean {
  const market = String(leg.market ?? "").toLowerCase();
  const selection = String(leg.selection ?? "").toLowerCase();
  const blob = `${market} ${selection}`;
  const compact = selection.replace(/[^12x]/g, "");
  return (
    (/double[\s_-]?chance|home\s*or\s*away/.test(blob) &&
      /home/.test(blob) &&
      /away/.test(blob) &&
      !/draw|\bx\b/.test(selection)) ||
    (/double[\s_-]?chance|home\s*or\s*away/.test(blob) && /^(12|21)$/.test(compact))
  );
}

function hasCautionDriver(leg: ScoredLeg): boolean {
  return (leg.drivers ?? []).some(
    (d) =>
      d.source === "market_drift_caution" ||
      (d.source === "line_movement" && (d.contribution_pp ?? 0) < 0) ||
      // Phase 1 — a material intel read AGAINST the leg (e.g. key injury,
      // dead-rubber) is a genuine caution, with a named cause.
      (d.source === "agent_intel" && (d.contribution_pp ?? 0) < 0),
  );
}

/** The named intel cause for a leg, if the desk-agent panel flagged one —
 *  used to replace the generic "Shaky read" with the real reason. */
function namedIntelReason(leg: ScoredLeg): string | null {
  const d = (leg.drivers ?? []).find(
    (x) => x.source === "agent_intel" && (x.contribution_pp ?? 0) < 0 && !!x.evidence,
  );
  return d?.evidence ? String(d.evidence).slice(0, 160) : null;
}

/** Coarse market direction for the cross-fixture correlation tier. */
function legDirection(
  leg: ScoredLeg,
): "home" | "away" | "over" | "under" | "yes" | "no" | "neutral" {
  const s = String(leg.selection ?? "").toLowerCase();
  if (/\bhome\b|^\s*1\s*$/.test(s)) return "home";
  if (/\baway\b|^\s*2\s*$/.test(s)) return "away";
  if (/\bover\b/.test(s)) return "over";
  if (/\bunder\b/.test(s)) return "under";
  if (/\b(yes|y)\b/.test(s)) return "yes";
  if (/\b(no|n)\b/.test(s)) return "no";
  return "neutral";
}

function dayKey(leg: ScoredLeg): string | null {
  const k = (leg as { kickoff_at?: string | null }).kickoff_at;
  if (!k) return null;
  const d = new Date(k);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

/** Two legs are correlated (duplicate-risk) when they share a same-fixture
 *  market correlation above the T1.3 threshold, OR sit in the same league +
 *  same day + same direction (cross-fixture). Pure. */
function legsCorrelated(a: ScoredLeg, b: ScoredLeg): boolean {
  if (Math.abs(pairCorrelation(a, b)) >= CORRELATION_THRESHOLD) return true;
  const la = String(a.league ?? "").toLowerCase();
  const lb = String(b.league ?? "").toLowerCase();
  if (!la || la !== lb) return false;
  const da = legDirection(a);
  const db = legDirection(b);
  if (da === "neutral" || da !== db) return false;
  const ka = dayKey(a);
  const kb = dayKey(b);
  if (!ka || !kb) return false; // need a confirmed same-day to avoid false +ve
  return ka === kb;
}

/** Indices of legs that are the WEAKER (lower-quality) member of at least one
 *  correlated relationship — those are the duplicate-risk removal candidates.
 *  The strongest leg of each correlated cluster is the anchor and is kept. */
function correlatedCandidateIndices(legs: ScoredLeg[]): Set<number> {
  const candidates = new Set<number>();
  for (let i = 0; i < legs.length; i++) {
    for (let j = i + 1; j < legs.length; j++) {
      if (!legsCorrelated(legs[i]!, legs[j]!)) continue;
      const weaker = computeQualityScore(legs[i]!) <= computeQualityScore(legs[j]!) ? i : j;
      candidates.add(weaker);
    }
  }
  return candidates;
}

/** Assign a leg to its highest-priority elimination tier, or null when it is
 *  not a ladder candidate. A near-lock favourite is never eliminated (same
 *  STRONG_FAVOURITE guard as the hard veto — keeping it is the point). Pure;
 *  exported for unit testing. */
export function classifyEliminationTier(
  leg: ScoredLeg,
  isCorrelated: boolean,
): EliminationTier | null {
  const pWin = qClamp(leg.pWin, 0, 1);
  const ev = typeof leg.expectedValue === "number" ? leg.expectedValue : 0;
  const modelEdge = hasModelEdge(leg);
  if (isHomeOrAwayDoubleChance(leg) && !modelEdge) return "home_or_away_trap";
  // PROTECTED near-lock — never eliminated. But a high win-rate only earns
  // protection when it has an ANALYTICAL basis: a model edge, or a real
  // Fair-Price read (confidence "high"). A merely short-priced market-implied
  // favourite in an unmodelled / exotic market (no edge, medium/low confidence
  // — e.g. a 50-leg "1 goal by 10 min" acca where every leg lands at 64-80%
  // purely from the bookmaker price) is NOT a near-lock; it's exactly the
  // edgeless filler the user is asking to prune, so it stays eligible.
  if (pWin >= STRONG_FAVOURITE && (leg.confidence === "high" || modelEdge)) {
    return null;
  }
  // 1) No edge — market-implied only, non-positive expectation.
  if (ev <= 0 && !modelEdge) return "no_edge";
  // 2) Weak selection — low win probability.
  if (pWin < WEAK_PWIN_FLOOR) return "weak_selection";
  // 3) Shaky — the model is genuinely unsure (wide CI / low conf / caution).
  //    Guarded to sub-favourite pWin so a genuine high-pWin read isn't tagged.
  if (
    pWin < STRONG_FAVOURITE &&
    (ciWidth(leg) >= SHAKY_CI_WIDTH || leg.confidence === "low" || hasCautionDriver(leg))
  ) {
    return "shaky_team";
  }
  // 4) Correlated duplicate-risk (weaker member of a correlated cluster).
  if (isCorrelated) return "correlated";
  return null;
}

/** Within-tier severity — higher value = remove sooner. */
function tierSeverity(leg: ScoredLeg, tier: EliminationTier): number {
  switch (tier) {
    case "home_or_away_trap":
      return 1;
    case "weak_selection":
      return 1 - qClamp(leg.pWin, 0, 1); // lowest pWin first
    case "shaky_team":
      return ciWidth(leg); // widest interval first
    case "no_edge":
    case "correlated":
      return 1 - computeQualityScore(leg); // lowest quality first
  }
}

function ladderReason(
  tier: EliminationTier,
  pWin: number,
  meanPWin: number,
  damageRatio: number,
  leg?: ScoredLeg,
): string {
  const tail = `removing improved slip hit probability by ${damageRatio.toFixed(1)}×.`;
  switch (tier) {
    case "home_or_away_trap":
      return `Home-or-Away draw trap — this Double Chance 12 leg only loses on the draw, which is exactly the football acca killer the short price hides; ${tail}`;
    case "no_edge":
      return `No STRIKZA edge — market-implied only at ${(pWin * 100).toFixed(0)}% with no model value backing it; ${tail}`;
    case "weak_selection":
      return `Weak selection — pWin ${(pWin * 100).toFixed(0)}% (slip average ${(meanPWin * 100).toFixed(0)}%); ${tail}`;
    case "shaky_team": {
      // Phase 1 — if the intel analyst named a cause (key injury, dead-rubber),
      // surface THAT instead of the generic high-variance line.
      const named = leg ? namedIntelReason(leg) : null;
      if (named) return `Intel concern — ${named}; ${tail}`;
      return `Shaky read — high model variance (low-confidence / wide interval) at ${(pWin * 100).toFixed(0)}% pWin; ${tail}`;
    }
    case "correlated":
      return `Correlated duplicate-risk — overlaps another kept leg (same fixture, or same league/day/side); ${tail}`;
  }
}

function ladderWarning(tier: EliminationTier, pWin: number, oddsTarget: OddsTarget | null): string {
  const need = oddsTarget ? ` to reach your ~${oddsTarget.value.toFixed(0)} odds target` : "";
  const label =
    tier === "home_or_away_trap"
      ? "Home-or-Away draw trap"
      : tier === "no_edge"
        ? "No clear edge"
        : tier === "weak_selection"
          ? `Weak (pWin ${(pWin * 100).toFixed(0)}%)`
          : tier === "shaky_team"
            ? "Shaky read"
            : "Correlated";
  return `${label} but kept — needed${need}.`;
}

interface OddsTarget {
  value: number;
  toleranceLo: number;
  toleranceHi: number;
}

function extractOddsTarget(plan: FilterPlan): OddsTarget | null {
  for (const tool of plan.tools) {
    if (tool.name === "target_combined_odds") {
      return {
        value: tool.value,
        toleranceLo: tool.value,
        toleranceHi: tool.value * (1 + tool.tolerance),
      };
    }
    if (tool.name === "cap_combined_odds") {
      return {
        value: tool.max,
        toleranceLo: 0,
        toleranceHi: tool.max,
      };
    }
  }
  return null;
}

interface WeakLinkPassResult {
  kept: ScoredLeg[];
  removed: DroppedLeg[];
  warnings: WeakLinkWarning[];
}

// Prune defaults (founder, 2026-06-04). "Get rid of the teams likely to kill
// this slip" must NOT gut the slip down to a handful — the documented failure.
// At most this share is removed, and never below the keep floor.
const PRUNE_DEFAULT_MAX_FRACTION = 0.4;
const PRUNE_DEFAULT_MIN_KEEP = 4;

/** Max number of legs the bounded prune may remove from an `n`-leg slip. Honours
 *  the user's max_fraction / min_keep, clamped by the absolute min-leg floor.
 *  Returns 0 when the slip is already at/below the keep floor. Pure. */
export function computePruneBudget(
  tool: { max_fraction?: number; min_keep?: number },
  n: number,
): number {
  const frac = qClamp(tool.max_fraction ?? PRUNE_DEFAULT_MAX_FRACTION, 0, 0.9);
  const minKeep = Math.max(Math.floor(tool.min_keep ?? PRUNE_DEFAULT_MIN_KEEP), WEAK_LINK_MIN_LEGS);
  const byFraction = Math.floor(frac * n);
  const byMinKeep = n - minKeep;
  return Math.max(0, Math.min(byFraction, byMinKeep));
}

function applyWeakLinkPass(
  legs: ScoredLeg[],
  oddsTarget: OddsTarget | null,
  retentionCap: number | null = null,
): WeakLinkPassResult {
  if (legs.length < WEAK_LINK_MIN_LEGS) {
    return { kept: [...legs], removed: [], warnings: [] };
  }

  const meanPWin = legs.reduce((s, l) => s + l.pWin, 0) / legs.length;
  const correlated = correlatedCandidateIndices(legs);

  // Classify every leg into its highest-priority elimination tier.
  interface Candidate {
    leg: ScoredLeg;
    index: number;
    tier: EliminationTier;
    severity: number;
    pWin: number;
    damageRatio: number;
  }
  const candidates: Candidate[] = [];
  legs.forEach((leg, index) => {
    const tier = classifyEliminationTier(leg, correlated.has(index));
    if (!tier) return;
    const p = Math.min(Math.max(leg.pWin, 0.001), 0.999);
    candidates.push({
      leg,
      index,
      tier,
      severity: tierSeverity(leg, tier),
      pWin: p,
      damageRatio: 1 / p,
    });
  });
  if (candidates.length === 0) {
    return { kept: [...legs], removed: [], warnings: [] };
  }

  // Ladder order: worst tier first; within a tier, most severe first.
  candidates.sort((a, b) => {
    const ta = LADDER_ORDER.indexOf(a.tier);
    const tb = LADDER_ORDER.indexOf(b.tier);
    if (ta !== tb) return ta - tb;
    return b.severity - a.severity;
  });

  const removed: DroppedLeg[] = [];
  const warnings: WeakLinkWarning[] = [];
  const removedIndices = new Set<number>();

  for (const candidate of candidates) {
    // Min-leg floor — never trim toward a Singles product.
    if (legs.length - removedIndices.size <= WEAK_LINK_MIN_LEGS) break;

    // Retention-cap mode (bounded prune, no odds target): remove the worst
    // legs in ladder order until the cap is hit, then stop. Nothing is
    // warned — the whole point is to prune conservatively, not gut the slip.
    if (oddsTarget === null && retentionCap !== null) {
      if (removedIndices.size >= retentionCap) break;
      removedIndices.add(candidate.index);
      removed.push({
        leg: candidate.leg,
        source_tool: "weak_link",
        tier: candidate.tier,
        reason: ladderReason(
          candidate.tier,
          candidate.pWin,
          meanPWin,
          candidate.damageRatio,
          candidate.leg,
        ),
      });
      continue;
    }

    // A numeric target is a delivery requirement. Remove weak links only while
    // the remaining source pool can still reach the bottom of its target
    // window. Open-ended quality requests keep their ordinary hard pruning.
    const trialKept = legs.filter(
      (_, index) => !removedIndices.has(index) && index !== candidate.index,
    );
    const trialOdds = computeCombinedOdds(trialKept);
    const canRemove =
      oddsTarget === null || (trialOdds !== null && trialOdds >= oddsTarget.toleranceLo);

    if (canRemove) {
      removedIndices.add(candidate.index);
      removed.push({
        leg: candidate.leg,
        source_tool: "weak_link",
        tier: candidate.tier,
        reason: ladderReason(
          candidate.tier,
          candidate.pWin,
          meanPWin,
          candidate.damageRatio,
          candidate.leg,
        ),
      });
    } else if (candidate.pWin < CARRIED_RISK_PWIN) {
      // "Carried risk" only applies to a leg that is genuinely MORE LIKELY TO
      // LOSE than win (pWin < 50%) yet had to stay to reach the target. A
      // short-priced favourite kept for the target (e.g. an edgeless 73% pick)
      // is likely to play through — flagging it "weak / carried risk" misleads
      // the user and erodes trust. Slip-level edgelessness is conveyed by the
      // coverage advisory, not by smearing every favourite. So such legs stay
      // kept WITHOUT a warning.
      const fixture =
        [candidate.leg.home_team, candidate.leg.away_team].filter(Boolean).join(" vs ") ||
        "Unknown";
      warnings.push({
        index: candidate.index,
        fixture,
        market: candidate.leg.market ?? "",
        selection: candidate.leg.selection ?? "",
        odds: candidate.leg.odds ?? 0,
        pWin: candidate.pWin,
        damage_ratio: candidate.damageRatio,
        improvement_factor: `${candidate.damageRatio.toFixed(1)}×`,
        tier: candidate.tier,
        reason: ladderWarning(candidate.tier, candidate.pWin, oddsTarget),
      });
    }
  }

  const finalKept = legs.filter((_, i) => !removedIndices.has(i));
  return { kept: finalKept, removed, warnings };
}

// ─── Per-tool implementations ──────────────────────────────────

interface OneToolResult {
  kept: ScoredLeg[];
  dropped: DroppedLeg[];
}

function applyOneTool(kept: ScoredLeg[], tool: FilterTool): OneToolResult {
  switch (tool.name) {
    case "filter_by_market":
      return filterByMarket(kept, tool);
    case "filter_by_odds_range":
      return filterByOddsRange(kept, tool);
    case "filter_by_league":
      return filterByLeague(kept, tool);
    case "filter_by_predicted_hit_probability":
      return filterByPredictedHitProbability(kept, tool);
    case "filter_by_predicted_ev":
      return filterByPredictedEv(kept, tool);
    case "select_top_n_by":
      return selectTopNBy(kept, tool);
    case "cap_combined_odds":
      return capCombinedOdds(kept, tool);
    case "target_combined_odds":
      return targetCombinedOdds(kept, tool);
    case "cap_leg_count":
      return capLegCount(kept, tool);
    case "filter_out_low_confidence":
      return filterOutLowConfidence(kept, tool);
    case "prune_risky_legs":
      // Declarative here — the bounded elimination ladder runs in Pass 1
      // (it needs the full pre-tool leg set to rank ticket-killers). Echoed
      // in `applied_tools` so the UI can show the intent.
      return { kept, dropped: [] };
    case "set_optimization_target":
      // Declarative — no direct effect. The intent parser already
      // adjusted other tools to honour the target. Echoed in
      // `applied_tools` so the UI can show it.
      return { kept, dropped: [] };
    case "decline":
      // Already handled at the top of applyFilterPlan, but guard
      // here defensively — if Claude emits decline mid-sequence,
      // empty the kept set.
      return {
        kept: [],
        dropped: kept.map((leg) => ({
          leg,
          source_tool: "decline",
          reason: tool.reason,
        })),
      };
  }
}

function filterByMarket(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "filter_by_market" }>,
): OneToolResult {
  const include = (tool.include ?? []).map((s) => s.toLowerCase());
  const exclude = (tool.exclude ?? []).map((s) => s.toLowerCase());
  const out: ScoredLeg[] = [];
  const drop: DroppedLeg[] = [];
  for (const leg of kept) {
    const market = (leg.market ?? "").toLowerCase();
    if (include.length > 0 && !include.some((m) => market.includes(m))) {
      drop.push({
        leg,
        source_tool: "filter_by_market",
        reason: `Market "${leg.market ?? "?"}" not in the keep-list (${tool.include?.join(", ")}).`,
      });
      continue;
    }
    if (exclude.length > 0 && exclude.some((m) => market.includes(m))) {
      drop.push({
        leg,
        source_tool: "filter_by_market",
        reason: `Market "${leg.market ?? "?"}" matched the drop-list (${tool.exclude?.join(", ")}).`,
      });
      continue;
    }
    out.push(leg);
  }
  return { kept: out, dropped: drop };
}

function filterByOddsRange(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "filter_by_odds_range" }>,
): OneToolResult {
  const out: ScoredLeg[] = [];
  const drop: DroppedLeg[] = [];
  for (const leg of kept) {
    const odds = leg.odds ?? 0;
    if (tool.min !== undefined && odds < tool.min) {
      drop.push({
        leg,
        source_tool: "filter_by_odds_range",
        reason: `Odds ${odds.toFixed(2)} below the minimum ${tool.min.toFixed(2)}.`,
      });
      continue;
    }
    if (tool.max !== undefined && odds > tool.max) {
      drop.push({
        leg,
        source_tool: "filter_by_odds_range",
        reason: `Odds ${odds.toFixed(2)} above the maximum ${tool.max.toFixed(2)}.`,
      });
      continue;
    }
    out.push(leg);
  }
  return { kept: out, dropped: drop };
}

function filterByLeague(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "filter_by_league" }>,
): OneToolResult {
  const include = (tool.include ?? []).map((s) => s.toLowerCase());
  const exclude = (tool.exclude ?? []).map((s) => s.toLowerCase());
  const out: ScoredLeg[] = [];
  const drop: DroppedLeg[] = [];
  for (const leg of kept) {
    const league = (leg.league ?? "").toLowerCase();
    if (include.length > 0 && !include.some((l) => league.includes(l))) {
      drop.push({
        leg,
        source_tool: "filter_by_league",
        reason: `League "${leg.league ?? "?"}" not in the keep-list.`,
      });
      continue;
    }
    if (exclude.length > 0 && exclude.some((l) => league.includes(l))) {
      drop.push({
        leg,
        source_tool: "filter_by_league",
        reason: `League "${leg.league ?? "?"}" matched the drop-list.`,
      });
      continue;
    }
    out.push(leg);
  }
  return { kept: out, dropped: drop };
}

function filterByPredictedHitProbability(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "filter_by_predicted_hit_probability" }>,
): OneToolResult {
  const out: ScoredLeg[] = [];
  const drop: DroppedLeg[] = [];
  for (const leg of kept) {
    if (leg.pWin < tool.min) {
      drop.push({
        leg,
        source_tool: "filter_by_predicted_hit_probability",
        reason: `Predicted win probability ${(leg.pWin * 100).toFixed(0)}% below the ${(tool.min * 100).toFixed(0)}% floor.`,
      });
      continue;
    }
    out.push(leg);
  }
  return { kept: out, dropped: drop };
}

function filterByPredictedEv(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "filter_by_predicted_ev" }>,
): OneToolResult {
  const out: ScoredLeg[] = [];
  const drop: DroppedLeg[] = [];
  for (const leg of kept) {
    if (leg.expectedValue < tool.min) {
      drop.push({
        leg,
        source_tool: "filter_by_predicted_ev",
        reason: `Expected value ${(leg.expectedValue * 100).toFixed(1)}% below the ${(tool.min * 100).toFixed(1)}% floor.`,
      });
      continue;
    }
    out.push(leg);
  }
  return { kept: out, dropped: drop };
}

function selectTopNBy(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "select_top_n_by" }>,
): OneToolResult {
  const scored = kept.map((leg) => ({ leg, score: extractCriterion(leg, tool.criterion) }));
  scored.sort((a, b) => b.score - a.score);
  const keepCount = Math.min(tool.n, scored.length);
  const out = scored.slice(0, keepCount).map((s) => s.leg);
  const drop: DroppedLeg[] = scored.slice(keepCount).map((s) => ({
    leg: s.leg,
    source_tool: "select_top_n_by",
    reason: `Not in the top ${tool.n} by ${tool.criterion}.`,
  }));
  return { kept: out, dropped: drop };
}

function extractCriterion(leg: ScoredLeg, criterion: "pWin" | "ev" | "odds"): number {
  switch (criterion) {
    case "pWin":
      return leg.pWin;
    case "ev":
      return leg.expectedValue;
    case "odds":
      return leg.odds ?? 0;
  }
}

function capCombinedOdds(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "cap_combined_odds" }>,
): OneToolResult {
  // Greedy: drop the LOWEST-QUALITY legs until combined odds fit under the
  // cap. Quality-first (N-Q1) so the near-locks survive and the shakiest
  // legs are shed first.
  const working = [...kept];
  const drop: DroppedLeg[] = [];
  while (working.length > 0) {
    const combined = computeCombinedOdds(working);
    if (combined === null || combined <= tool.max) break;
    working.sort((a, b) => computeQualityScore(a) - computeQualityScore(b));
    const removed = working.shift()!;
    drop.push({
      leg: removed,
      source_tool: "cap_combined_odds",
      reason: `Removed (lowest quality) to bring combined odds below ${tool.max.toFixed(1)}.`,
    });
  }
  return { kept: working, dropped: drop };
}

function targetCombinedOdds(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "target_combined_odds" }>,
): OneToolResult {
  // Fulfilment-first subset selection. Inside the accepted target band, fewer
  // negative-EV legs and stronger retained quality outrank tiny odds-distance
  // differences. For large slips a bounded log-odds dynamic program is used.
  if (kept.length === 0) return { kept, dropped: [] };

  const targetLo = tool.value;
  const targetHi = tool.value * (1 + tool.tolerance);
  const fulfillmentRank = (combined: number): number =>
    combined >= targetLo && combined <= targetHi ? 2 : combined >= targetLo ? 1 : 0;
  const sumQ = (legs: readonly ScoredLeg[]): number =>
    legs.reduce((s, l) => s + computeQualityScore(l), 0);
  const negativeEvCount = (legs: readonly ScoredLeg[]): number =>
    legs.reduce((count, leg) => count + (Number(leg.expectedValue) < 0 ? 1 : 0), 0);

  let bestSubset: ScoredLeg[] | null = null;

  if (kept.length <= 12) {
    let bestRank = -1;
    let bestSum = -Infinity;
    let bestDist = Infinity;
    let bestNegativeEv = Infinity;
    const n = kept.length;
    for (let mask = 1; mask < 1 << n; mask++) {
      const subset: ScoredLeg[] = [];
      for (let i = 0; i < n; i++) if ((mask >> i) & 1) subset.push(kept[i]!);
      const combined = computeCombinedOdds(subset);
      if (combined === null) continue;
      const rank = fulfillmentRank(combined);
      const s = sumQ(subset);
      const dist = Math.abs(combined - tool.value);
      const negativeEv = negativeEvCount(subset);
      let better = false;
      if (rank !== bestRank) {
        better = rank > bestRank;
      } else if (rank === 2) {
        better =
          negativeEv < bestNegativeEv ||
          (negativeEv === bestNegativeEv &&
            (s > bestSum + 1e-9 || (Math.abs(s - bestSum) <= 1e-9 && dist < bestDist - 1e-9)));
      } else {
        better =
          dist < bestDist - 1e-9 ||
          (Math.abs(dist - bestDist) <= 1e-9 &&
            (negativeEv < bestNegativeEv || (negativeEv === bestNegativeEv && s > bestSum)));
      }
      if (bestSubset === null || better) {
        bestSubset = subset;
        bestRank = rank;
        bestSum = s;
        bestDist = dist;
        bestNegativeEv = negativeEv;
      }
    }
  } else {
    interface OddsState {
      legs: ScoredLeg[];
      logOdds: number;
      quality: number;
      negativeEv: number;
    }
    const protectedLegs = kept.filter((leg) =>
      leg.drivers?.some((driver) =>
        String((driver as { signal?: string }).signal ?? "").includes("convergence"),
      ),
    );
    const protectedSet = new Set(protectedLegs);
    const optionalLegs = kept.filter((leg) => !protectedSet.has(leg));
    const seedLog = protectedLegs.reduce((sum, leg) => sum + Math.log(leg.odds ?? 1), 0);
    const seedQuality = sumQ(protectedLegs);
    const targetLog = Math.log(tool.value);
    const maxLegLog = Math.max(0, ...kept.map((leg) => Math.log(leg.odds ?? 1)));
    const maxLog = Math.log(targetHi) + maxLegLog;
    const bucketStep = Math.max(0.0025, maxLog / 10_000);
    let states: OddsState[] = [
      {
        legs: [...protectedLegs],
        logOdds: seedLog,
        quality: seedQuality,
        negativeEv: negativeEvCount(protectedLegs),
      },
    ];

    for (const leg of optionalLegs) {
      const legLog = Math.log(leg.odds ?? 1);
      const legQuality = computeQualityScore(leg);
      const buckets = new Map<number, OddsState>();
      for (const state of states) {
        for (const next of [
          state,
          {
            legs: [...state.legs, leg],
            logOdds: state.logOdds + legLog,
            quality: state.quality + legQuality,
            negativeEv: state.negativeEv + (Number(leg.expectedValue) < 0 ? 1 : 0),
          },
        ]) {
          if (next.logOdds > maxLog) continue;
          const key = Math.round(next.logOdds / bucketStep);
          const prior = buckets.get(key);
          if (
            !prior ||
            next.negativeEv < prior.negativeEv ||
            (next.negativeEv === prior.negativeEv &&
              (next.quality > prior.quality + 1e-9 ||
                (Math.abs(next.quality - prior.quality) <= 1e-9 &&
                  Math.abs(next.logOdds - targetLog) < Math.abs(prior.logOdds - targetLog))))
          ) {
            buckets.set(key, next);
          }
        }
      }
      states = [...buckets.values()];
    }

    let bestRank = -1;
    let bestDistance = Infinity;
    let bestQuality = -Infinity;
    let bestNegativeEv = Infinity;
    for (const state of states) {
      if (state.legs.length === 0) continue;
      const combined = Math.exp(state.logOdds);
      const rank = fulfillmentRank(combined);
      const distance = Math.abs(combined - tool.value);
      let better = bestSubset === null || rank > bestRank;
      if (bestSubset !== null && rank === bestRank) {
        better =
          rank === 2
            ? state.negativeEv < bestNegativeEv ||
              (state.negativeEv === bestNegativeEv &&
                (state.quality > bestQuality + 1e-9 ||
                  (Math.abs(state.quality - bestQuality) <= 1e-9 &&
                    distance < bestDistance - 1e-9)))
            : distance < bestDistance - 1e-9 ||
              (Math.abs(distance - bestDistance) <= 1e-9 &&
                (state.negativeEv < bestNegativeEv ||
                  (state.negativeEv === bestNegativeEv && state.quality > bestQuality)));
      }
      if (better) {
        bestSubset = state.legs;
        bestRank = rank;
        bestDistance = distance;
        bestQuality = state.quality;
        bestNegativeEv = state.negativeEv;
      }
    }
  }

  const keptSet = new Set(bestSubset ?? []);
  const out = kept.filter((l) => keptSet.has(l));
  const drop: DroppedLeg[] = kept
    .filter((l) => !keptSet.has(l))
    .map((leg) => ({
      leg,
      source_tool: "target_combined_odds",
      reason: `Trimmed to reach ~${tool.value.toFixed(0)} odds — lower-quality legs dropped before the near-locks.`,
    }));
  return { kept: out, dropped: drop };
}

function capLegCount(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "cap_leg_count" }>,
): OneToolResult {
  if (kept.length <= tool.max) return { kept, dropped: [] };
  // Cap by QUALITY (the strongest legs survive) — N-Q1.
  const sorted = [...kept].sort((a, b) => computeQualityScore(b) - computeQualityScore(a));
  const keep = sorted.slice(0, tool.max);
  const keepSet = new Set(keep);
  const out = kept.filter((l) => keepSet.has(l));
  const drop: DroppedLeg[] = kept
    .filter((l) => !keepSet.has(l))
    .map((leg) => ({
      leg,
      source_tool: "cap_leg_count",
      reason: `Cap of ${tool.max} legs — kept the highest-quality subset.`,
    }));
  return { kept: out, dropped: drop };
}

function filterOutLowConfidence(
  kept: ScoredLeg[],
  tool: Extract<FilterTool, { name: "filter_out_low_confidence" }>,
): OneToolResult {
  if (!tool.enabled) return { kept, dropped: [] };
  const out: ScoredLeg[] = [];
  const drop: DroppedLeg[] = [];
  for (const leg of kept) {
    if (leg.confidence === "low") {
      drop.push({
        leg,
        source_tool: "filter_out_low_confidence",
        reason: "Scorer confidence is low — not enough signal coverage.",
      });
      continue;
    }
    out.push(leg);
  }
  return { kept: out, dropped: drop };
}

// ─── Quality-first selection (N-Q1) ─────────────────────────────
//
// The executor used to assemble slips ODDS-FIRST: to hit a target like
// "~200 odds" it kept whatever legs multiplied closest to the number,
// which systematically DROPPED strong favourites (a 1.30 near-lock barely
// moves the odds product) and KEPT shaky high-odds legs. That is exactly
// backwards — a near-certain winner is the most valuable leg in an
// accumulator. computeQualityScore() ranks a leg by how strongly the
// signals back it WINNING; selection now maximises retained quality.

function qClamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, Number.isFinite(x) ? x : lo));
}

/** Quality score Q ∈ [0,1]. Higher = a leg we want to KEEP. pWin
 *  dominates (a near-lock is gold), with EV, scorer confidence, and the
 *  net signed signal support as secondary tilts. Pure; from fields
 *  already on the ScoredLeg (no new data). */
export function computeQualityScore(leg: ScoredLeg): number {
  const pWin = qClamp(leg.pWin, 0, 1);
  const ev = typeof leg.expectedValue === "number" ? leg.expectedValue : 0;
  // EV mapped so −10% → 0, 0% → 0.33, +20% → 1.
  const evTerm = qClamp((ev + 0.1) / 0.3, 0, 1);
  const confTerm = leg.confidence === "high" ? 1 : leg.confidence === "medium" ? 0.6 : 0.3;
  // Net signed signal support (percentage points), each weighted by the
  // substrate's confidence in that signal. ±10pp spans the full range.
  const driverNet = (leg.drivers ?? []).reduce(
    (s, d) => s + (d.contribution_pp ?? 0) * (d.weight ?? 0),
    0,
  );
  const driverTerm = qClamp(0.5 + driverNet / 20, 0, 1);
  // VALUE-AWARE (founder, Norway-Over-3 case): a leg's EDGE matters, not just how
  // big a favourite it is. The old 0.65·pWin weighting ranked low-odds near-locks
  // above genuinely good high-odds value legs, so the odds-target trim cut +EV
  // legs (Norway: pWin 54.6%, EV +31%, +15pp research) in favour of edgeless
  // market-implied near-locks. Rebalanced so EV + model drivers + confidence
  // carry real weight: a model-backed value leg now outranks an edgeless
  // favourite, while a TRULY strong favourite (high pWin + real engine edge)
  // still ranks top.
  const base = 0.4 * pWin + 0.35 * evTerm + 0.1 * confTerm + 0.15 * driverTerm;
  // Protection bump: a leg STRIKZA actively likes — a real model/research read
  // (confidence ≥ medium) with a positive edge — should never be trimmed before
  // an edgeless market-implied leg. Small additive guard, capped at 1.
  const modelBacked =
    (leg.confidence === "high" || leg.confidence === "medium") && (ev > 0 || driverNet > 0);
  return qClamp(base + (modelBacked ? 0.05 : 0), 0, 1);
}

/** A genuine value-trap that should never reach the assembled slip: a
 *  clearly negative-EV leg that is NOT a near-lock. Strong favourites are
 *  never vetoed on EV alone — the bookmaker margin makes a true 80% pick
 *  slightly −EV, but you still want it in the slip. Pure. */
// Tuned (founder): protect MORE near-locks (0.62 → 0.58 — anything 58%+ is
// immune to the EV veto) and shed slightly more clearly −EV shaky legs
// (−0.05 → −0.04).
const EV_HARD_FLOOR = -0.04;
const EV_HARD_FLOOR_MEDIUM = -0.02; // tighter when we have some signal
const STRONG_FAVOURITE = 0.58;
// Low-confidence legs (market-implied only) survive the veto ONLY if
// they're near-locks. Without model signal we can't call them traps
// on EV alone, but we won't blindly keep mid-odds noise either.
const LOW_CONF_PWIN_FLOOR = 0.55;

export function isHardVeto(leg: ScoredLeg): boolean {
  // A near-certain favourite is never vetoed — keeping it is the point.
  if (qClamp(leg.pWin, 0, 1) >= STRONG_FAVOURITE) return false;

  // No signal coverage (market-implied only, confidence "low").
  // Original tennis-edge-case patch was a blanket bypass — which let
  // 30+ mid-odds market-implied legs slip into football assemblers
  // and produced 0.000 pHit slips (see autopsy 2026-05-28: bust run).
  // New rule: low-confidence still bypasses EV math (the −0.05 floor
  // is just the bookmaker overround, not a real trap signal), but
  // requires the leg be a genuine favourite — anything below 55%
  // implied is shed before assembly. Protects the tennis case (a
  // 70% market-implied favourite still survives) while killing the
  // accumulator-bloat that was busting users.
  if (leg.confidence === "low") {
    return qClamp(leg.pWin, 0, 1) < LOW_CONF_PWIN_FLOOR;
  }

  // Medium confidence: we have some signal — tighter EV floor.
  const ev = typeof leg.expectedValue === "number" ? leg.expectedValue : 0;
  if (leg.confidence === "medium") return ev < EV_HARD_FLOOR_MEDIUM;

  // High confidence — full EV floor.
  return ev < EV_HARD_FLOOR;
}

// Minimum acceptable hit probability for a "quality-first" assembled
// slip. Below 1%, the maths say users will bust ≥99% of the time —
// even great combined odds can't make up for it. When the kept set
// falls below this floor, we trim the LOWEST-pWin legs one at a time
// until we either clear the floor or have only `MIN_KEPT_AFTER_TRIM`
// legs left (we never trim to single-leg, that's a Singles product).
export const MIN_HIT_PROBABILITY = 0.01; // 1%
export const MIN_KEPT_AFTER_TRIM = 4;

/** Pure: trim the lowest-pWin legs until hit probability ≥ floor
 *  or kept count hits the floor. Returns the trimmed set + dropped. */
export function enforceHitProbabilityFloor(
  kept: ScoredLeg[],
  floor = MIN_HIT_PROBABILITY,
  minKept = MIN_KEPT_AFTER_TRIM,
): { kept: ScoredLeg[]; dropped: DroppedLeg[] } {
  const trimmed: DroppedLeg[] = [];
  let current = [...kept];
  // Sort once by pWin descending — we always pop from the end.
  current.sort((a, b) => b.pWin - a.pWin);
  while (current.length > minKept) {
    const product = current.reduce((p, l) => p * l.pWin, 1);
    if (product >= floor) break;
    const popped = current.pop()!;
    trimmed.push({
      leg: popped,
      source_tool: "hit_probability_floor",
      reason:
        `Trimmed to clear the ${(floor * 100).toFixed(0)}% hit-probability ` +
        `floor — leg pWin ${(popped.pWin * 100).toFixed(0)}% was the weakest ` +
        `link in a ${current.length + 1}-leg slip whose combined chance had ` +
        `dropped below the wins-are-rare threshold.`,
    });
  }
  return { kept: current, dropped: trimmed };
}

// ─── Honest coverage advisory (SE-LADDER) ──────────────────────
//
// When NONE of the kept legs are model-backed, the whole slip is just the
// bookmaker's prices — STRIKZA has no edge anywhere in it. Pruning trims the
// worst legs but cannot create value that isn't there. We say so plainly
// rather than hand back a rearranged bust that looks blessed. A leg is
// "model-backed" when it carries a real edge driver or a Fair-Price ("high")
// read. Pure; returns null the moment at least one kept leg has an edge.
export function computeCoverageAdvisory(
  kept: readonly ScoredLeg[],
): { level: "none"; kept_with_edge: number; kept_total: number; message: string } | null {
  if (kept.length < 3) return null; // too small to be a meaningful "mega-acca"
  const withEdge = kept.filter((l) => hasModelEdge(l) || l.confidence === "high").length;
  if (withEdge > 0) return null;
  return {
    level: "none",
    kept_with_edge: 0,
    kept_total: kept.length,
    message:
      "No STRIKZA edge on this slip — none of these fixtures are in STRIKZA's " +
      "model coverage, so every leg is just the bookmaker's price. Trimming " +
      "removes the worst, but it can't add value that isn't there.",
  };
}

// ─── Aggregate metric helpers ──────────────────────────────────

/** Product of legs' decimal odds. Null when empty. */
export function computeCombinedOdds(legs: readonly ScoredLeg[]): number | null {
  if (legs.length === 0) return null;
  let product = 1;
  for (const leg of legs) {
    if (typeof leg.odds !== "number" || !Number.isFinite(leg.odds)) return null;
    product *= leg.odds;
  }
  return product;
}

/** Product of per-leg pWin (assumes legs are independent — an
 *  approximation; correlated outcomes would shift this). Null when
 *  empty. */
export function computeHitProbability(legs: readonly ScoredLeg[]): number | null {
  if (legs.length === 0) return null;
  let product = 1;
  for (const leg of legs) product *= leg.pWin;
  return product;
}

/** Slip-level EV = combined_odds × hit_probability − 1. Null when empty. */
export function computeExpectedValue(legs: readonly ScoredLeg[]): number | null {
  const combined = computeCombinedOdds(legs);
  const hit = computeHitProbability(legs);
  if (combined === null || hit === null) return null;
  return combined * hit - 1;
}
